type MonthObject = {
  name: string,
  id: string,
  available: boolean,
  isNextYear: boolean,
};

const currentDate = new Date();
const currentYear = currentDate.getFullYear();

const convertToMonthName = (dateStr: string) => {
  const parts = dateStr.split('/');

  if (parts.length !== 2) throw Error('Invalid input format');

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = parseInt(parts[0], 10);

  if (month < 1 || month > 12) throw Error('Invalid month');

  const monthName = monthNames[month - 1];

  return monthName;
};

const convertMonthIdToObject = (id: string, available = false) => ({
  available,
  isNextYear: parseInt(id.split('/')[1], 10) > currentYear,
  id,
  name: convertToMonthName(id),
});

const getDefaultMonths = (withApiData = false) => {
  const currentMonth = currentDate.getMonth() + 1; // Adding 1 to get the current month as 1-based index

  const months: MonthObject[] = [{
    available: !withApiData,
    id: `${currentMonth.toString().padStart(2, '0')}/${currentYear}`,
    isNextYear: false,
    name: 'Soon',
  }];

  for (let i = 0; i < 12; i++) {
    const date = new Date(currentYear, currentMonth - 1 + i, 1); // Subtracting 1 to get the correct month

    months.push({
      available: !withApiData,
      id: `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`,
      isNextYear: date.getFullYear() > currentYear,
      name: date.toLocaleString('en-AU', { month: 'short' }),
    });
  }

  return months;
};

const parseDate = (dateStr: string) => {
  const [month, year] = dateStr.split('/');

  return new Date(Number(year), Number(month) - 1, 1);
};

const getGapMonths = (dataArray, startId, endId) => {
  const startDate = parseDate(startId);
  const endDate = parseDate(endId);

  const missingIds: string[] = [];

  while (startDate <= endDate) {
    const formattedDate = startDate.toLocaleDateString('en-US', {
      month: '2-digit',
      year: 'numeric',
    });

    if (!dataArray.some((item) => item.id === formattedDate)) missingIds.push(formattedDate);

    startDate.setMonth(startDate.getMonth() + 1);
  }

  return missingIds;
};

const sortByDate = (unsortedArray: string[] | MonthObject[]) => {
  const sortedArray = unsortedArray.sort((a, b) => {
    const dateA = new Date(parseDate(a.id || a));
    const dateB = new Date(parseDate(b.id || b));

    return dateA.getTime() - dateB.getTime();
  });

  return sortedArray;
};

const getMonths = (availableMonths: string[] = []) => {
  // 1. Get default 12 months
  const mergedMonths = getDefaultMonths(availableMonths.length > 0);

  // 2. Add months that we get from API endpoint to the default ones
  if (availableMonths.length > 0) {
    const sortedAvailableMonths = sortByDate(availableMonths);

    sortedAvailableMonths.forEach((monthId) => {
      const existingMonth = mergedMonths.filter((month) => month.id === monthId);

      if (existingMonth.length > 0) {
        existingMonth.forEach((month) => month.available = true); // eslint-disable-line no-return-assign
      } else {
        mergedMonths.push(convertMonthIdToObject(monthId, true));
      }
    });
  }

  // 3. Calculate and add the "gap" months - all the unavailable months between the ones that are available
  const gapMonths = getGapMonths(mergedMonths, mergedMonths[0].id, mergedMonths[mergedMonths.length - 1].id);

  if (gapMonths.length > 0) {
    gapMonths.forEach((gapMonth) => mergedMonths.push(convertMonthIdToObject(gapMonth)));
  }

  // 4. Add the new year separators
  const sortedMonths = sortByDate(mergedMonths);
  let nextYear = currentYear + 1;

  sortedMonths.forEach((item, index, array) => {
    if (item.name === 'Dec') {
      array.splice(index + 1, 0, nextYear);

      nextYear = nextYear + 1;
    }
  });

  return sortedMonths;
};

export default getMonths;
